import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Sidebar from "@/components/Sidebar";
import ChatBubble from "@/components/ChatBubble";
import MobileBottomNav from "@/components/MobileBottomNav";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ChatInterface from "@/pages/ChatInterface";
import StoreManager from "@/pages/StoreManager";
import StoreDetail from "@/pages/StoreDetail";
import StaffManager from "@/pages/StaffManager";
import Financials from "@/pages/Financials";
import Documents from "@/pages/Documents";
import PlaybookViewer from "@/pages/PlaybookViewer";
import PlaybookList from "@/pages/PlaybookList";
import BusinessBiblePage from "@/pages/BusinessBible";
import EquipmentManager from "@/pages/EquipmentManager";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import PublicFurniture from "@/pages/PublicFurniture";
import Marketing from "@/pages/Marketing";
import Tasks from "@/pages/Tasks";
import Products from "@/pages/Products";
import EmailDigests from "@/pages/EmailDigests";
import ProviderDirectory from "@/pages/ProviderDirectory";
import { useState, useRef } from "react";
import { Loader2, Command } from "lucide-react";

function ProtectedRoutes() {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const chatBubbleRef = useRef(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 noise-bg">
        <div className="text-center">
          <div className="w-14 h-14 bg-stone-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-7 h-7 text-stone-50 animate-spin" />
          </div>
          <p className="text-sm text-stone-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const handleMobileChatClick = () => {
    setChatOpen(true);
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      <main className="flex-1 ml-0 md:ml-56 min-h-screen pb-20 md:pb-0">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/chat" element={<ChatInterface />} />
          <Route path="/stores" element={<StoreManager />} />
          <Route path="/stores/:storeId" element={<StoreDetail />} />
          <Route path="/staff" element={<StaffManager />} />
          <Route path="/financials" element={<Financials />} />
          <Route path="/providers" element={<ProviderDirectory />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/equipment" element={<EquipmentManager />} />
          <Route path="/playbooks" element={<PlaybookList />} />
          <Route path="/playbooks/business-bible" element={<BusinessBiblePage />} />
          <Route path="/playbooks/:playbookId" element={<PlaybookViewer />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/products" element={<Products />} />
          <Route path="/email-digests" element={<EmailDigests />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/team" element={<Team />} />
          <Route path="/marketing" element={<Marketing />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <ChatBubble externalOpen={chatOpen} onOpenChange={setChatOpen} />
      <MobileBottomNav onChatClick={handleMobileChatClick} />
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return null;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/furniture/:shareToken" element={<PublicFurniture />} />
      <Route path="/*" element={<ProtectedRoutes />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App noise-bg">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
